import { isToday, isTomorrow } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  ConsultTimePeriodEnum,
  ExpertConsultsSortByEnum,
  ExpertGlobalRoleEnum,
  GetExpertConsultsByAttributeQuery,
  GetExpertConsultsQuery,
  SortDirectionEnum
} from "@/typing/Generated";
import ExpertConsultRow from "@components/expert/ExpertConsultRow";
import styles from "@components/expert/ExpertConsultsPage.module.css";
import ConsultReassignForm from "@components/forms/ConsultReassignForm";
import { LoadMoreArgs } from "@components/InfiniteScroll";
import Modal from "@components/Modal";
import Table, { TableHeader } from "@components/Table";
import Icon from "@components/text/Icon";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";

type ConsultType = NonNullable<
  NonNullable<NonNullable<GetExpertConsultsByAttributeQuery["expertConsults"]["edges"]>[number]>["node"]
>;

type PageInfo = NonNullable<NonNullable<GetExpertConsultsQuery["expertConsults"]["pageInfo"]>>;

type Props = {
  consults: ConsultType[];
  expertId?: string | null;
  fetchMore?: (args: LoadMoreArgs) => Promise<any>;
  pageInfo: PageInfo;
  setSortBy: (sortBy: ExpertConsultsSortByEnum) => void;
  setSortDirection: (sortDirection: SortDirectionEnum) => void;
  skipScroll?: boolean;
  sortBy: ExpertConsultsSortByEnum;
  sortDirection: SortDirectionEnum;
  totalCount: number;
  upcoming?: ConsultTimePeriodEnum | null;
};

const ExpertConsultsTable = ({
  consults,
  expertId,
  fetchMore,
  pageInfo,
  setSortBy,
  setSortDirection,
  skipScroll = false,
  sortBy,
  sortDirection,
  totalCount,
  upcoming
}: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();
  const expertExplanation = useMemo(
    () => (
      <Icon
        color="primary"
        hoverContent={intl.formatMessage({ id: "pages.expert.consults.consultsExplained" })}
        icon="questionCircle"
        spacingStart
      />
    ),
    [intl]
  );
  const [reassigningConsult, setReassigningConsult] = useState<ConsultType>();
  const reassignConsultOpener = useOpener();

  const tableHeaders: TableHeader<ExpertConsultsSortByEnum>[] = useMemo(() => {
    const headers = [
      { columnSortBy: ExpertConsultsSortByEnum.date },
      { messageKey: "pages.expert.consults.tableHeadings.time" },
      { columnSortBy: ExpertConsultsSortByEnum.client_name },
      { messageKey: "pages.expert.consults.tableHeadings.language_interpretation" },
      { columnSortBy: ExpertConsultsSortByEnum.loved_one_name },
      { columnSortBy: ExpertConsultsSortByEnum.role, extraInfo: expertId ? expertExplanation : undefined },
      { messageKey: "pages.expert.consults.tableHeadings.finalized" }
    ];
    if (myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager) {
      headers.push({ messageKey: "dictionary.actions" });
    }
    return headers;
  }, [expertExplanation, expertId, myExpert?.expertGlobalRole]);

  const date = new Date();

  const dateByString = useCallback(
    (date: Date) => {
      if (isToday(date)) {
        return intl.formatMessage({ id: "pages.expert.consults.days.today" });
      }
      if (isTomorrow(date)) {
        return intl.formatMessage({ id: "pages.expert.consults.days.tomorrow" });
      }
      return intl.formatDate(date, { weekday: "long" });
    },
    [intl]
  );

  const handleReassign = useCallback(
    (consult: ConsultType) => {
      setReassigningConsult(consult);
      reassignConsultOpener.open();
    },
    [reassignConsultOpener]
  );

  return (
    <>
      <div className={styles.tableContainer}>
        <div className={`${styles.consultCount} ${skipScroll ? styles.noMargin : ""}`}>
          <FormattedMessage id="pages.expert.consults.consultCount" values={{ count: totalCount }} />
        </div>
      </div>
      <Table<ExpertConsultsSortByEnum>
        fetchMore={fetchMore}
        headerKey="pages.expert.consults.tableHeadings"
        headers={tableHeaders}
        pageInfo={pageInfo}
        setSortBy={setSortBy}
        setSortDirection={setSortDirection}
        skipScroll={skipScroll}
        skipSpacing={skipScroll}
        sortBy={sortBy}
        sortDirection={sortDirection}
      >
        {consults.map(consult => (
          <ExpertConsultRow
            consult={consult}
            date={date}
            dateByString={dateByString}
            key={consult.id + Math.random.toString()}
            onReassign={handleReassign}
            upcoming={upcoming}
          />
        ))}
      </Table>

      <Modal
        opener={reassignConsultOpener}
        title={intl.formatMessage({
          id: "models.consult.reassignConsult"
        })}
      >
        {({}) => (
          <>
            {reassigningConsult && (
              <ConsultReassignForm
                consult={reassigningConsult}
                onCancel={() => {
                  reassignConsultOpener.close();
                }}
                onSuccess={() => {
                  reassignConsultOpener.close();
                }}
              />
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default ExpertConsultsTable;
